import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogContent, DialogFooter, DialogTitle,
} from '../../../components/base/Dialog/Dialog';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FilterTemplateDTO from '../../../dto/FilterTemplateDTO';
import { getFirstFormError, getResponseError, setFormError } from '../../../utils/utils';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import { PAGE_TYPE_LIST } from './filter-settings';
import GroupList from './GroupList';
import FieldList from './FieldList';
import OptionList from './OptionList';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { createFilterTemplate, updateFilterTemplate } from '../actions';
import { getFilterTemplateData } from '../reducer';

export default function FilterTemplateForm(props) {
  const {
    loadData = () => {},
    onClose = () => {},
    handleView = () => {},
  } = props;

  const { filterTemplate } = useSelector(getFilterTemplateData);

  const dispatch = useDispatch();
  const [openedGroup, setOpenedGroup] = useState(null);
  const [openedFields, setOpenedFields] = useState(null);
  const [currentPage, setCurrentPage] = useState(filterTemplate?.page ? filterTemplate.page : null);
  const [searchGroup, setSearchGroup] = useState('');
  const [searchField, setSearchField] = useState('');
  const [groupList, setGroupList] = useState(filterTemplate?.filters ? JSON.parse(JSON.stringify(filterTemplate.filters)) : []);

  // eslint-disable-next-line no-bitwise
  const hashCode = s => s.split('').reduce((a, b) => (((a << 5) - a) + b.charCodeAt(0)) | 0, 0);

  const setChildren = children => {
    const index = groupList.findIndex(g => g.id === openedGroup);
    groupList[index].children = children;
    setGroupList(groupList);
    setValue('hash', hashCode(JSON.stringify(groupList)), { shouldDirty: true });
  };

  const setOptions = option => {
    const newGroup = [...groupList];
    const index = groupList.findIndex(g => g.id === openedGroup);
    const fieldIndex = newGroup[index].children.findIndex(g => g.dto.name === openedFields);
    if (newGroup[index].children[fieldIndex].options?.length) {
      const optionIndex = newGroup[index].children[fieldIndex].options.findIndex(o => o.value === option.value);
      newGroup[index].children[fieldIndex].options[optionIndex].enable = !newGroup[index].children[fieldIndex].options[optionIndex].enable;
      newGroup[index].children[fieldIndex].enable = newGroup[index].children[fieldIndex].options.some(o => o.enable);
      newGroup[index].enable = newGroup[index].children.some(o => o.enable);
    }
    setGroupList(newGroup);
    setValue('hash', hashCode(JSON.stringify(newGroup)), { shouldDirty: true });
  };

  const handleFieldChange = field => {
    const newGroup = [...groupList];
    const index = groupList.findIndex(g => g.id === openedGroup);
    const fieldIndex = newGroup[index].children.findIndex(g => g.dto.name === field.dto.name);
    const isEnable = newGroup[index].children[fieldIndex].enable;
    newGroup[index].children[fieldIndex].enable = !isEnable;
    if (newGroup[index].children[fieldIndex].options?.length) {
      newGroup[index].children[fieldIndex].options = newGroup[index].children[fieldIndex].options.map(o => ({ ...o, enable: !isEnable }));
    }
    newGroup[index].enable = newGroup[index].children.some(o => o.enable);
    setGroupList(newGroup);
    setValue('hash', hashCode(JSON.stringify(newGroup)), { shouldDirty: true });
  };

  const {
    control,
    getValues,
    setValue,
    formState: { errors, isDirty },
    setError,
    setFocus,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: filterTemplate?.id ? { ...filterTemplate, hash: hashCode(JSON.stringify(filterTemplate?.filters ? filterTemplate.filters : [])) } : {},
  });
  const DTO = new FilterTemplateDTO();
  const attributes = DTO.getAttributesByName();

  useEffect(() => {
    const subscription = watch(values => {
      if (values.page !== currentPage) {
        const selectedPage = PAGE_TYPE_LIST.find(option => option.value === values.page);
        const selectedOptionList = (selectedPage ? selectedPage.filter || [] : []).map((g, i) => {
          g.sortIndex = i;
          g.children = g.children?.map((c, ci) => ({ ...c, sortIndex: ci }));
          return g;
        });
        setCurrentPage(values.page);
        setGroupList(selectedOptionList);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleSave = () => {
    const error = getFirstFormError(errors);
    if (error) {
      errorNotification('Сохранение шаблона фильтра', 'Ошибка заполнения формы');
      const { ref } = error;
      if (ref && ref.name) {
        setFocus(ref.name);
      }
      return null;
    }
    const values = getValues();
    delete values.hash;
    const action = filterTemplate?.id ? updateFilterTemplate : createFilterTemplate;
    dispatch(action({ ...values, filters: JSON.parse(JSON.stringify(groupList)), is_active: filterTemplate ? filterTemplate?.is_active : true }))
      .then(() => {
        loadData();
        onClose();
        successNotification('Сохранение шаблона фильтра', 'Шаблон сохранен');
      })
      .catch(response => {
        setFormError(response, setError);
        errorNotification('Сохранение шаблона фильтра', getResponseError({ error: response && response.message ? response.message : '' }));
      });
  };

  const currentGroup = groupList?.find(g => g.id === openedGroup);
  const currentField = currentGroup?.children?.find(f => f.dto.name === openedFields) || null;

  return (
    <Dialog
      className="filter-template-form"
      onClose={onClose}
      open
    >
      <DialogTitle
        header={(
          <div className="d-flex align-items-center">
            <div>{filterTemplate?.id ? 'Редактирование шаблона фильтра ' : 'Создание шаблона фильтра '}</div>
            <div className="ms-auto notification-block">
              <TooltipWithPortal
                content={<Icons customIcon icon="info-circle" className="notification-circle" />}
                classNamePortal="notification-help-portal"
                tooltipContent={(
                  <div className="notification-help-wrapper">
                    <div className="notification-help-header">Заполнение формы</div>
                    <div className="notification-help-text">
                      Установка флажка (чек-бокса) для выбранного раздела инициирует автоматический выбор всех полей, включённых в данный раздел. Аналогичный механизм применяется при выборе поля со списком вариантов — при выборе такого поля автоматически отмечается соответствующий набор опций
                    </div>
                  </div>
              )}
                classNameWrapper="notification-tooltip-wrapper"
                className="notification-tooltip"
              />
            </div>
          </div>
)}
        title={<Icons onClick={onClose} customIcon icon="close" className="close" />}
      />
      <DialogContent className="filter-template-form-content">
        <div className="f3">Применение шаблона фильтра</div>
        <div className="filter-template-info mt-3">
          <FieldDTO
            control={control}
            component={TextInputWithLabel}
            dtoObject={attributes.name}
            variant="material"
            className="me-3"
          />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={attributes.page}
            usePortal
            options={PAGE_TYPE_LIST}
            addPlaceholder
            variant="material"
            withLabel
          />
        </div>
        <div className="mt-3 d-flex align-items-center">
          <div className="f3">Форма шаблона</div>
          <div
            className="preview-button ms-auto"
            onClick={() => handleView({
              name: getValues('name'), page: currentPage, filters: groupList, edit: true,
            })()}
          >Предварительный просмотр
          </div>
        </div>
        <div className="mt-3 filter-template-fields">
          <div className="ftf-column filter-template-group">
            <div className="f4">Разделы</div>
            {currentPage ? <GroupList searchGroup={searchGroup} groupList={groupList} setGroupList={setGroupList} setOpenedGroup={setOpenedGroup} openedGroup={openedGroup} /> : (
              <div className="empty-fields-wrapper">
                Список пунктов отобразиться, когда будет выбрано &quot;Применение шаблона фильтра&quot;
              </div>
            )}
          </div>
          <div className="ftf-column filter-template-field">
            <div className="f4">Список полей в выбранном разделе</div>
            {currentGroup ? <FieldList key={currentGroup.id} searchField={searchField} setChildren={setChildren} group={currentGroup} setOpenedFields={setOpenedFields} openedFields={openedFields} handleFieldChange={handleFieldChange} /> : (
              <div className="empty-fields-wrapper">
                Список пунктов отобразиться, когда будет выбрано поле со стрелочкой в левом столбце
              </div>
            )}
          </div>
          <div className="ftf-column filter-template-option">
            <div className="f4">Список пунктов для полей с выбором вариантов</div>
            {currentField ? <OptionList key={`${currentGroup.id}-${currentField.dto.name}`} setOptions={setOptions} currentField={currentField} /> : (
              <div className="empty-fields-wrapper">
                Список пунктов отобразиться, когда будет выбрано  поле со стрелочкой в центральном столбце
              </div>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogFooter className="hff-footer">
        <div className="hff-footer-buttons">
          <Button className="hff-clear-data" variant="secondary" onClick={onClose}>Отменить</Button>
          <Button disabled={!isDirty} className="hff-clear-show" onClick={handleSave}>Сохранить</Button>
        </div>
      </DialogFooter>
    </Dialog>
  );
}
